<template>
  <v-dialog :value="value" persistent max-width="600" @input="$emit('update:value', $event)">
    <v-card class="modalCard">
      <v-toolbar flat dark class="main">
        <v-toolbar-title>Update Summary</v-toolbar-title>
        <v-spacer />
        <v-btn
          text
          color="grey darken-2"
          @click="closeModal"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-4">
        <div v-if="response">
          <!-- Old simple response format: {created: 5} -->
          <template v-if="isLegacyResponse">
            <v-row class="mb-2">
              <v-col cols="12">
                <div class="text-subtitle-1 font-weight-medium">
                  Order lines created: {{ response.created || 0 }}
                </div>
              </v-col>
            </v-row>
          </template>
          <!-- New detailed response from generate_order_qty -->
          <template v-else-if="isDetailedResponse">
            <v-row class="mb-2">
              <v-col cols="12">
                <div class="text-subtitle-1 font-weight-medium">
                  Total order lines created: {{ response.total_orders_created }}
                </div>
              </v-col>
            </v-row>
            <v-row v-if="(response.orders_created || []).length" class="mb-3">
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mb-1">Items with new suggested orders</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">Item</th>
                      <th class="text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in response.orders_created" :key="'created-' + i">
                      <td>{{ item.item_id }}</td>
                      <td>{{ item.item_desc || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-row v-if="(response.components_adjusted || []).length" class="mb-3">
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mb-1">Component orders reduced (shipper generated)</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">Item</th>
                      <th class="text-left">Description</th>
                      <th class="text-right">Previous cases</th>
                      <th class="text-right">New cases</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in response.components_adjusted" :key="'adj-' + i">
                      <td>{{ item.item_id }}</td>
                      <td>{{ item.item_desc }}</td>
                      <td class="text-right">{{ item.previous_cases }}</td>
                      <td class="text-right">{{ item.new_cases }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-row v-if="(response.components_removed || []).length" class="mb-3">
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mb-1">Component orders removed (shipper covers)</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">Item</th>
                      <th class="text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in response.components_removed" :key="'rem-' + i">
                      <td>{{ item.item_id }}</td>
                      <td>{{ item.item_desc || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-row v-if="(response.shippers_added || []).length" class="mb-3">
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mb-1">Shipper items added</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">Item</th>
                      <th class="text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in response.shippers_added" :key="'add-' + i">
                      <td>{{ item.item_id }}</td>
                      <td>{{ item.item_desc || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
            <v-row v-if="(response.shippers_removed || []).length" class="mb-3">
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mb-1">Shipper items removed</div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">Item</th>
                      <th class="text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in response.shippers_removed" :key="'srem-' + i">
                      <td>{{ item.item_id }}</td>
                      <td>{{ item.item_desc || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </template>
        </div>
        <div v-else class="text-body-1">
          No summary information available.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          class="text-capitalize font-weight-medium"
          @click="closeModal"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'UpdateSummaryModal',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    response: {
      type: Object,
      default: null
    }
  },
  computed: {
    isLegacyResponse() {
      return this.response && this.response.created && !this.isDetailedResponse
    },
    isDetailedResponse() {
      return this.response && typeof this.response.total_orders_created === 'number'
    }
  },
  methods: {
    closeModal() {
      this.$emit('update:value', false)
    }
  }
}
</script>

<style scoped>
.modalCard {
  max-width: 600px;
}

.main {
  background-color: #1e1e1e;
}
</style>
